import { useDispatch, useSelector } from 'react-redux';

import withUploadModal from '@/Components/Modals/withUploadModal';
import { Mixpanel } from '@/Mixpanel';
import { datasetSliceName, userDataSliceName } from '@/redux/constants';
import { showModal } from '@/redux/reducers/modalsSlice';
import {
  MODAL_COMBINE_FILES,
  MODAL_DELETE_FILE,
  MODAL_MOVE_FILE,
  MODAL_REMOVE_ACCESS,
  ROUTE_EXPORTS,
  ROUTE_HOME,
  ROUTE_IMPORTS,
  SHARED_WITH_ME,
} from '@/Utils/constants';
import { FILE_STATUS_ERROR, FILE_TYPE_EXPORTER, FILE_TYPE_EXTERNAL } from '@/Utils/fileConstants';
import clsx from 'clsx';

import NewFileMenu from '@/Components/NewFileMenu/NewFileMenu';
import { Button } from '@/Components/UI/Button/Button';
import useOnClickOutsideHook from '@/hooks/useOnClickOutside';
import useViewportProvider from '@/hooks/useViewportProvider';
import LibraryAutomation from '@/pages/DatasetPage/HeaderControlGroup/LibraryAutomation';
import { isReadOnly } from '@/Utils/sharing';
import { useRef, useState } from 'react';
import LibraryFilePath from './FilePath/LibraryFilePath';
import './index.scss';

const HeaderControlGroup = ({ openUploadModal, title = '' }) => {
  const dispatch = useDispatch();
  const {
    selectedFiles,
    currentDataset,
    filePath,
    librarySearchResult,
    filesSizePercentage,
    currentFile,
    sortingData,
  } = useSelector((state) => state[datasetSliceName]);
  const { isViewerAnalyst } = useSelector((state) => state[userDataSliceName]);
  const { isMobile } = useViewportProvider();

  const addNewFileMenu = useRef(null);
  const [isOpenAddMenu, setIsOpenAddMenu] = useState(false);

  const _setIsOpenAddMenu = () => {
    setIsOpenAddMenu(!isOpenAddMenu);
  };

  useOnClickOutsideHook(isOpenAddMenu, addNewFileMenu, _setIsOpenAddMenu);

  const isHomeTabActive = window.location.pathname.includes(ROUTE_HOME);
  const isSharedWithMeTabActive = currentDataset === SHARED_WITH_ME;
  const isExportsTab = window.location.pathname.includes(ROUTE_EXPORTS);
  const isScheduledImportsTab = window.location.pathname.includes(ROUTE_IMPORTS);
  const isLiveQuery = filePath[filePath.length - 1]?.metadata?.Type === FILE_TYPE_EXTERNAL;

  const isButtonsDisabled = filesSizePercentage >= 100;

  const isAddButtonDisabled = () => {
    const isAtSharedWithMeRoot = isSharedWithMeTabActive && sortingData?.currentDirectory === '';
    const readOnly = isReadOnly(currentFile);
    const tooDeepFolderStructure = filePath?.length >= 9;
    return tooDeepFolderStructure || isAtSharedWithMeRoot || readOnly || isButtonsDisabled;
  };

  const showMoveFileModal = () => {
    dispatch(
      showModal({
        name: MODAL_MOVE_FILE,
        props: {
          file: selectedFiles?.[0],
          folderName: filePath[filePath?.length - 1]?.metadata?.FileName || '',
          folderId: filePath[filePath?.length - 1]?.metadata?.FileUuid || '',
          isMovingSelected: true,
        },
      })
    );
  };

  const disabledMoveFolder =
    !selectedFiles ||
    !selectedFiles.length ||
    selectedFiles.some((file) => file.Type === FILE_TYPE_EXTERNAL);
  const disabledDeleteItem = !selectedFiles || !selectedFiles.length;
  const disabledCombineButton = selectedFiles.some(
    (file) =>
      file.Status === FILE_STATUS_ERROR ||
      file.Type === FILE_TYPE_EXPORTER ||
      file.Type === FILE_TYPE_EXTERNAL
  );

  const onClickCombineFilesBtn = () => {
    dispatch(showModal({ name: MODAL_COMBINE_FILES }));
    Mixpanel.track('Combine Files Clicked');
  };

  const onDeleteButtonClick = () => {
    // if we are deleting files shared with me, just opt-out on all checked files
    if (isSharedWithMeTabActive) {
      return dispatch(showModal({ name: MODAL_REMOVE_ACCESS, props: { isMultiple: true } }));
    }

    dispatch(
      showModal({
        name: MODAL_DELETE_FILE,
        props: {
          filesToDelete: selectedFiles,
        },
      })
    );
  };

  return (
    <div
      className={clsx(
        'datasets-header__control !static !md:sticky flex flex-col gap-2 my-4 justify-start',
        isMobile && 'hidden'
      )}
    >
      {isHomeTabActive && !librarySearchResult ? (
        !isViewerAnalyst ? (
          <LibraryAutomation openUploadModal={openUploadModal} />
        ) : null
      ) : (
        <LibraryFilePath title={title} />
      )}

      <div className='flex items-start justify-between w-full'>
        <div>
          {!librarySearchResult && !isHomeTabActive && !isScheduledImportsTab && !isLiveQuery ? (
            <div className='flex items-center gap-2 p-1 mt-2 border rounded md:flex border-ui-200 bg-ui-secondary'>
              {!isSharedWithMeTabActive && !isExportsTab && !isViewerAnalyst && (
                <Button
                  dataCy='combine-btn'
                  className={clsx(disabledCombineButton ? 'pointer-events-none' : '')}
                  disabled={disabledCombineButton}
                  onClick={onClickCombineFilesBtn}
                  iconName='arrows-in-line-horizontal'
                  color='shadow'
                  variant='ghost'
                >
                  Combine
                </Button>
              )}

              {!isSharedWithMeTabActive && !isExportsTab && (
                <Button
                  dataCy='move-btn'
                  disabled={disabledMoveFolder}
                  className={clsx(disabledMoveFolder ? 'pointer-events-none' : '')}
                  onClick={showMoveFileModal}
                  color='shadow'
                  variant='ghost'
                  iconName='arrows-out-cardinal'
                >
                  Move
                </Button>
              )}

              <Button
                dataCy='deleteItemBtn'
                className={clsx(disabledDeleteItem ? 'pointer-events-none' : '')}
                disabled={disabledDeleteItem}
                onClick={onDeleteButtonClick}
                iconName='trash'
                color='shadow'
                variant='ghost'
              >
                {isSharedWithMeTabActive ? 'Remove Access' : 'Delete'}
              </Button>
            </div>
          ) : null}
        </div>

        {!librarySearchResult &&
        !isExportsTab &&
        !isHomeTabActive &&
        !isScheduledImportsTab &&
        !isLiveQuery &&
        !isViewerAnalyst ? (
          <div className='flex items-center gap-2 pt-3'>
            {isOpenAddMenu ? (
              <div ref={addNewFileMenu}>
                <NewFileMenu toggleMenu={_setIsOpenAddMenu} onClickUpload={() => {}} />
              </div>
            ) : null}

            <Button
              onClick={_setIsOpenAddMenu}
              dataCy='add-new-btn'
              color='sunrise'
              disabled={isAddButtonDisabled()}
              iconName='plus-circle'
              className='w-40'
            >
              New
            </Button>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default withUploadModal(HeaderControlGroup);
